import { useEffect } from 'react'
import type { FunctionComponent } from 'react'

import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

import { Layout } from 'components/layouts/Layout'
import { AppContextProvider } from 'context/AppContext'
import { RefProvider } from 'context/RefContext'
import { gaInit, gaSendPageview } from 'utils/helpers/analytics'
import { getIsIOS } from 'utils/helpers/ua'
import { sentryInit } from 'utils/sentry'

sentryInit()

gaInit()

const isIOS = getIsIOS()

const App: FunctionComponent = () => {
  const {
    i18n: { language },
  } = useTranslation()

  useEffect(() => {
    // Disable pinch zoom on iOS.
    // iOS Pinch and zoom on sites causes site reloads and crashes on most sites.
    // https://bugs.webkit.org/show_bug.cgi?id=172206
    // We only disable on iOS as disabling on android will cause issues with the
    // browsers drag to refresh feature.
    let touchMoveHandler: ((event: TouchEvent) => void) | null = null

    if (isIOS) {
      touchMoveHandler = (event: TouchEvent) => {
        // @ts-ignore - iOS event property only
        if (event.scale !== 1) {
          event.preventDefault()
        }
      }

      document.addEventListener('touchmove', touchMoveHandler, {
        passive: false,
      })
    }

    gaSendPageview(location.pathname)

    // Cleanup function to remove event listener
    return () => {
      if (isIOS && touchMoveHandler) {
        document.removeEventListener('touchmove', touchMoveHandler)
      }
    }
  }, [isIOS])

  return (
    <AppContextProvider>
      <HelmetProvider context={{}}>
        <Helmet
          // Set lang to the current UI language
          // N.B. content fields will be set to the appropriate content
          // language as required
          htmlAttributes={{ lang: language }}
        />
        <RefProvider>
          <Layout>
            <Outlet />
          </Layout>
        </RefProvider>
      </HelmetProvider>
    </AppContextProvider>
  )
}

export default App
